import { useTranslation } from "react-i18next";
import { useAlertStore, Alert as AlertType } from "../stores/alertStore";

const alertStyles = {
  success: {
    container: "bg-green-50 border-green-200 text-green-800",
    icon: "text-green-500",
    IconComponent: ({ className }: { className?: string }) => (
      <span className={className} style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_3d85a50c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_3d85a50c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
    ),
  },
  error: {
    container: "bg-red-50 border-red-200 text-red-800",
    icon: "text-red-500",
    IconComponent: ({ className }: { className?: string }) => (
      <span className={className} style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_a3d284a4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_a3d284a4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
    ),
  },
  warning: {
    container: "bg-yellow-50 border-yellow-200 text-yellow-800",
    icon: "text-yellow-500",
    IconComponent: ({ className }: { className?: string }) => (
      <span className={className} style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_56f0eef4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_56f0eef4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
    ),
  },
  info: {
    container: "bg-blue-50 border-blue-200 text-blue-800",
    icon: "text-blue-500",
    IconComponent: ({ className }: { className?: string }) => (
      <span className={className} style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_31cfdd0f.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_31cfdd0f.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
    ),
  },
};

interface AlertItemProps {
  alert: AlertType;
  onDismiss: (id: string) => void;
}

function AlertItem({ alert, onDismiss }: AlertItemProps) {
  const { t } = useTranslation("common");
  const style = alertStyles[alert.type];
  const Icon = style.IconComponent;

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg ${style.container} animate-slide-in-right`}
      role="alert"
    >
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${style.icon}`} />
      <div className="flex-1 min-w-0">
        {alert.title && (
          <h4 className="font-semibold text-sm mb-1">{alert.title}</h4>
        )}
        <p className="text-sm">{alert.message}</p>
      </div>
      <button
        onClick={() => onDismiss(alert.id)}
        className="flex-shrink-0 p-1 rounded-md hover:bg-black/5 transition-colors"
        aria-label={String(t("actions.dismissAlert"))}
      >
        <span className="w-4 h-4" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_f52ff36b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_f52ff36b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
      </button>
    </div>
  );
}

export function AlertContainer() {
  const { alerts, dismissAlert } = useAlertStore();

  return (
    <div
      className="fixed top-4 right-4 left-4 sm:left-auto sm:max-w-md z-[200] flex flex-col gap-2 pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      {alerts.map((alert) => (
        <div key={alert.id} className="pointer-events-auto">
          <AlertItem alert={alert} onDismiss={dismissAlert} />
        </div>
      ))}
    </div>
  );
}

// Utility hook for easy alert usage
export function useAlert() {
  const showAlert = useAlertStore((state) => state.showAlert);

  return {
    success: (message: string, title?: string, duration?: number) =>
      showAlert({ type: "success", message, title, duration }),
    error: (message: string, title?: string, duration?: number) =>
      showAlert({ type: "error", message, title, duration }),
    warning: (message: string, title?: string, duration?: number) =>
      showAlert({ type: "warning", message, title, duration }),
    info: (message: string, title?: string, duration?: number) =>
      showAlert({ type: "info", message, title, duration }),
  };
}
